import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';

import { HOME_URL } from './constants';
import CENTRALIZED_CORPORATE_GET_CHILDREN_ORGS_NAVIGATION_QUERY from './graphql/corporate-get-children-org-navigation-query.v5.graphql';
import type {
  CentralizedCorporateGetChildrenOrgsNavigationQuery,
  CentralizedCorporateGetChildrenOrgsNavigationQueryVariables,
} from './graphql/types/CentralizedCorporateGetChildrenOrgsNavigationQuery';

export type TChildrenCompany = {
  id: number;
  name: string;
  slug: string;
  homeUrl: string;
};

export type TUseCorporateOrganizationsProps = {
  slug: string;
  search?: string;
  pageSize?: number;
  skip?: boolean;
};

export type TUseCorporateOrganizationsResponse = ReturnType<
  typeof useCorporateOrganizations
>;

export const useCorporateOrganizations = ({
  slug,
  search,
  pageSize = 8,
  skip,
}: TUseCorporateOrganizationsProps) => {
  const [fetchingMore, setFetchingMore] = useState<boolean>(false);

  const { data, error, refetch, fetchMore, loading } = useQuery<
    CentralizedCorporateGetChildrenOrgsNavigationQuery,
    CentralizedCorporateGetChildrenOrgsNavigationQueryVariables
  >(CENTRALIZED_CORPORATE_GET_CHILDREN_ORGS_NAVIGATION_QUERY, {
    variables: {
      input: {
        slug,
        pagination: {
          after: null,
          // TODO: Implement infinite scroll at packages/compass.layouts/src/sidebar-v2/components/sidebar-brand-selector.tsx
          first: 100, //pageSize,
        },
        search: {
          search,
        },
      },
    },
    context: {
      clientName: 'v5',
    },
    skip,
    fetchPolicy: 'cache-first',
    // nextFetchPolicy: 'cache-and-network',
    // notifyOnNetworkStatusChange: true,
  });

  const result = data?.corporation?.navigation_data?.navigation_data;

  const organizations = useMemo(() => {
    return result?.__typename ===
      'HRISCorporationsGetChildrenOrgNavigationQueryPayload'
      ? result.organizations.map(org => ({
          id: org.id,
          name: org.name,
          slug: org.slug,
          homeUrl: HOME_URL.replace('[slug]', org.slug),
        }))
      : [];
  }, [result]);

  const handleFetchMore = () => {
    if (
      data?.corporation?.navigation_data?.navigation_data?.__typename !==
      'HRISCorporationsGetChildrenOrgNavigationQueryPayload'
    ) {
      return;
    }

    if (!data?.corporation?.navigation_data?.navigation_data?.has_next_page) {
      return;
    }

    setFetchingMore(true);

    return fetchMore({
      variables: {
        input: {
          slug,
          pagination: {
            after:
              data?.corporation?.navigation_data?.navigation_data?.end_cursor,
            first: pageSize,
          },
          ...(search && {
            search: {
              search,
            },
          }),
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (
          !fetchMoreResult ||
          fetchMoreResult?.corporation?.navigation_data?.navigation_data
            ?.__typename !==
            'HRISCorporationsGetChildrenOrgNavigationQueryPayload'
        ) {
          return prev;
        }

        const prevPayload = prev.corporation?.navigation_data?.navigation_data;
        const newPayload =
          fetchMoreResult.corporation?.navigation_data?.navigation_data;

        if (
          prevPayload?.__typename !==
            'HRISCorporationsGetChildrenOrgNavigationQueryPayload' ||
          newPayload?.__typename !==
            'HRISCorporationsGetChildrenOrgNavigationQueryPayload'
        ) {
          return prev;
        }

        return {
          ...prev,
          corporation: {
            ...prev.corporation,
            navigation_data: {
              ...prev.corporation?.navigation_data,
              navigation_data: {
                ...newPayload,
                organizations: [
                  ...prevPayload.organizations,
                  ...newPayload.organizations,
                ],
              },
            },
          },
        };
      },
    })
      .catch(console.error)
      .finally(() => {
        setFetchingMore(false);
      });
  };

  const handleRefetch = () => {
    refetch();
  };

  const currentOrganization =
    result?.__typename ===
    'HRISCorporationsGetChildrenOrgNavigationQueryPayload'
      ? {
          id: result.current_org.id,
          name: result.current_org.name,
          slug: result.current_org.slug,
          homeUrl: HOME_URL.replace('[slug]', result.current_org.slug),
        }
      : null;

  const isCurrentParentOrg =
    result?.__typename ===
    'HRISCorporationsGetChildrenOrgNavigationQueryPayload'
      ? result.is_current_parent_org
      : false;

  const parentOrganizationId =
    result?.__typename ===
    'HRISCorporationsGetChildrenOrgNavigationQueryPayload'
      ? result.parent_org_id
      : null;

  const isCorporation =
    result?.__typename ===
    'HRISCorporationsGetChildrenOrgNavigationQueryPayload'
      ? result.is_corporation
      : false;

  if (skip) {
    return {
      loading: false as const,
      fetchingMore: false,
      error: false,
      organizations: null,
      isCorporation: false,
      isCurrentParentOrg: null,
      parentOrganizationId: null,
      currentOrganization: null,
      refetch: handleRefetch,
      fetchMore: handleFetchMore,
      isCorporationEmpty: false,
    };
  }

  if (loading && !fetchingMore) {
    return {
      loading: true as const,
      fetchingMore: false,
      error: false,
      organizations: null,
      isCorporation: false,
      isCurrentParentOrg: null,
      parentOrganizationId: null,
      currentOrganization: null,
      refetch: handleRefetch,
      fetchMore: handleFetchMore,
      isCorporationEmpty: false,
    };
  }

  if (
    error ||
    result?.__typename !==
      'HRISCorporationsGetChildrenOrgNavigationQueryPayload'
  ) {
    return {
      loading: false as const,
      fetchingMore: false,
      error: true,
      organizations: null,
      isCorporation: false,
      isCurrentParentOrg: null,
      parentOrganizationId: null,
      currentOrganization: null,
      refetch: handleRefetch,
      fetchMore: handleFetchMore,
      isCorporationEmpty: false,
    };
  }

  return {
    loading: false as const,
    fetchingMore,
    error: false,
    hasMore: result?.has_next_page,
    count: result?.total_count,
    organizations,
    isCorporation,
    isCurrentParentOrg,
    currentOrganization,
    parentOrganizationId,
    refetch: handleRefetch,
    fetchMore: handleFetchMore,
    isCorporationEmpty: !search && organizations.length === 0,
  };
};
